// src/pages/UserDetails.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box, Typography, Paper, CircularProgress, Avatar, Chip, Stack, Button,
    Grid, Tab, Tabs, IconButton, Snackbar, Alert, AlertColor, Dialog,
    DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
    OutlinedInput, ListItemText, Checkbox, TextField, Tooltip, Divider,
} from "@mui/material";
import {
    ArrowBack as BackIcon, Phone as PhoneIcon, Email as EmailIcon,
    Home as HomeIcon, School as SchoolIcon, CardMembership as GrantIcon,
    Devices as DevicesIcon, CheckCircle as ActiveIcon, RadioButtonUnchecked as InactiveIcon,
    CalendarToday as CalIcon, Person as PersonIcon, Star as StarIcon,
    Schedule as ScheduleIcon, Delete as DeleteIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

import { getUserDetails, UserDetailsResponse, getFullUrl, deleteUser } from "../services/userService";
import { getMediums, getSubjects, getAllSubjectsAdmin } from "../services/subjectService";
import { grantSubscription, revokeSubscription } from "../services/subscriptionService";
import { DetailShimmer } from "../components/Shimmer";

const PRIMARY = "#174143";
const ACCENT = "#3d9ea4";

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(14px); }
  to   { opacity:1; transform:translateY(0); }
`;

const USER_COLORS = [
    "linear-gradient(135deg,#667eea,#764ba2)",
    "linear-gradient(135deg,#4facfe,#00f2fe)",
    "linear-gradient(135deg,#43e97b,#38f9d7)",
    "linear-gradient(135deg,#f093fb,#f5576c)",
    "linear-gradient(135deg,#fa709a,#fee140)",
    "linear-gradient(135deg,#a18cd1,#fbc2eb)",
];

function getInitials(name?: string): string {
    if (!name) return "?";
    return name.split(" ").map(p => p[0]).join("").toUpperCase().slice(0, 2);
}

function formatDate(iso: string): string {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDateOnly(iso: string): string {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function isExpired(iso?: string): boolean {
    if (!iso) return false;
    return new Date(iso) < new Date();
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
    return (
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", py: 1.25, px: 2, borderRadius: 2, "&:hover": { background: "#f8fafc" }, transition: "background 0.15s" }}>
            <Box sx={{ color: ACCENT, mt: 0.15, flexShrink: 0 }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.63rem" }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 500, mt: 0.2 }}>
                    {value || <span style={{ color: "#94a3b8" }}>—</span>}
                </Typography>
            </Box>
        </Box>
    );
}

export default function UserDetailsPage(): React.ReactElement {
    const { id } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState<UserDetailsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [tabIndex, setTabIndex] = useState(0);
    const [snackbar, setSnackbar] = useState<{ type: AlertColor; msg: string } | null>(null);

    // Grant dialog
    const [grantOpen, setGrantOpen] = useState(false);
    const [grantLoading, setGrantLoading] = useState(false);
    const [availableSubjects, setAvailableSubjects] = useState<any[]>([]);
    const [grantForm, setGrantForm] = useState<{ subjectIds: number[]; expiryDate: string }>({ subjectIds: [], expiryDate: "" });

    // Revoke dialog
    const [revokeTarget, setRevokeTarget] = useState<{ subjectId: number; subjectName: string } | null>(null);
    const [revokeLoading, setRevokeLoading] = useState(false);

    // Delete dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        loadData();
        getMediums().catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadData = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const data = await getUserDetails(Number(id));
            setUser(data);
        } catch {
            setSnackbar({ type: "error", msg: "Failed to load user details" });
        } finally { setLoading(false); }
    };

    const handleOpenGrant = async () => {
        if (!user?.profile) return;
        setGrantOpen(true);
        setGrantLoading(true);
        setGrantForm({ subjectIds: [], expiryDate: "" });
        setAvailableSubjects([]);
        try {
            // Prefer class-filtered list when the user has a class. Otherwise fall
            // back to the full admin catalog so new subjects always show up here.
            let all: any[] = [];
            if (user.profile.classId) {
                const subs = await getSubjects({ classId: user.profile.classId });
                all = Array.isArray(subs) ? subs : [];
            }
            if (!all.length) {
                const adminList = await getAllSubjectsAdmin(0, 500).catch(() => []);
                all = Array.isArray(adminList) ? adminList : (adminList?.content ?? []);
            }
            const subscribedIds = new Set(user.subscriptions.map((s: any) => s.id));
            setAvailableSubjects(all.filter((s: any) => !subscribedIds.has(s.id)));
        } catch {
            setSnackbar({ type: "error", msg: "Failed to load subjects." });
        } finally { setGrantLoading(false); }
    };

    const handleRevokeSubject = async () => {
        if (!revokeTarget || !user?.profile) return;
        setRevokeLoading(true);
        try {
            await revokeSubscription({ userId: user.profile.id, subjectIds: [revokeTarget.subjectId] });
            setSnackbar({ type: "success", msg: `Access to "${revokeTarget.subjectName}" revoked.` });
            setRevokeTarget(null);
            loadData();
        } catch (err: any) {
            setSnackbar({ type: "error", msg: err?.message || "Failed to revoke access." });
        } finally { setRevokeLoading(false); }
    };

    const handleSubmitGrant = async () => {
        if (!user?.profile) return;
        if (grantForm.subjectIds.length === 0 || !grantForm.expiryDate) {
            setSnackbar({ type: "error", msg: "Please select subjects and an expiry date." });
            return;
        }
        setGrantLoading(true);
        try {
            await grantSubscription({
                userId: user.profile.id,
                subjectIds: grantForm.subjectIds,
                expiryDate: new Date(grantForm.expiryDate).toISOString(),
            });
            setSnackbar({ type: "success", msg: "Access granted successfully!" });
            setGrantOpen(false);
            loadData();
        } catch (err: any) {
            setSnackbar({ type: "error", msg: err.message || "Operation failed." });
        } finally { setGrantLoading(false); }
    };

    // ── Loading ───────────────────────────────────────────
    if (loading) return (
        <Box sx={{ minHeight: "100vh", background: "#f5f6fa", p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1000, mx: "auto" }}>
                <DetailShimmer />
            </Box>
        </Box>
    );
    if (!user) return (
        <Box sx={{ p: 6, textAlign: "center" }}>
            <PersonIcon sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">User not found</Typography>
            <Button onClick={() => navigate("/users")} sx={{ mt: 2 }}>Back to Users</Button>
        </Box>
    );

    const profile = user.profile;
    const gradient = USER_COLORS[profile.id % USER_COLORS.length];
    const isAdmin = profile.role === "ROLE_ADMIN";
    const sessionCount = profile.sessions?.length || 0;

    return (
        <Box sx={{ minHeight: "100vh", background: "#f5f6fa", p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1000, mx: "auto" }}>

                {/* ── Back button ── */}
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate("/users")}
                    sx={{ mb: 2.5, color: PRIMARY, fontWeight: 700, borderRadius: 2, "&:hover": { background: "rgba(23,65,67,0.06)" } }}
                >
                    Back to Users
                </Button>

                {/* ── Profile hero card ── */}
                <Paper elevation={0} sx={{
                    mb: 3, borderRadius: 4, overflow: "hidden",
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, #2a6b6f 60%, ${ACCENT} 100%)`,
                    color: "#fff", animation: `${fadeUp} 0.4s ease-out`, position: "relative",
                }}>
                    <Box sx={{ position: "absolute", top: -50, right: -30, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
                    <Box sx={{ position: "absolute", bottom: -30, right: 150, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.07)", pointerEvents: "none" }} />

                    <Box sx={{ p: { xs: 3, md: 4 }, display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", position: "relative" }}>
                        {/* Avatar */}
                        <Avatar
                            src={getFullUrl(profile.profilePhotoUrl)}
                            sx={{
                                width: 80, height: 80, flexShrink: 0,
                                background: gradient, fontSize: "1.6rem", fontWeight: 800,
                                border: "3px solid rgba(255,255,255,0.3)",
                                boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
                            }}
                        >
                            {!profile.profilePhotoUrl && getInitials(profile.displayName)}
                        </Avatar>

                        {/* Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.3 }}>
                                {profile.displayName}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.3 }}>
                                {profile.email}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
                                <Chip label={`#${profile.id}`} size="small"
                                    sx={{ background: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, fontSize: "0.72rem" }} />
                                <Chip label={isAdmin ? "Admin" : "Student"} size="small"
                                    sx={{ background: isAdmin ? "rgba(251,191,36,0.35)" : "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, fontSize: "0.72rem" }} />
                                {profile.isSubscribed && (
                                    <Chip icon={<StarIcon sx={{ fontSize: "12px !important", color: "#fff !important" }} />} label="Premium" size="small"
                                        sx={{ background: "rgba(67,233,123,0.35)", color: "#fff", fontWeight: 700, fontSize: "0.72rem" }} />
                                )}
                                {profile.className && (
                                    <Chip label={profile.className} size="small"
                                        sx={{ background: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 600, fontSize: "0.72rem" }} />
                                )}
                            </Stack>
                        </Box>

                        {/* Action buttons */}
                        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
                            <Button
                                variant="contained"
                                startIcon={<GrantIcon />}
                                onClick={handleOpenGrant}
                                sx={{
                                    background: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700, borderRadius: 2,
                                    border: "1px solid rgba(255,255,255,0.35)", backdropFilter: "blur(4px)",
                                    px: 3, py: 1.25,
                                    "&:hover": { background: "rgba(255,255,255,0.3)" },
                                }}
                            >
                                Grant Access
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<DeleteIcon />}
                                onClick={() => setDeleteOpen(true)}
                                sx={{
                                    background: "rgba(239,68,68,0.25)", color: "#fff", fontWeight: 700, borderRadius: 2,
                                    border: "1px solid rgba(239,68,68,0.45)", backdropFilter: "blur(4px)",
                                    px: 3, py: 1.25,
                                    "&:hover": { background: "rgba(239,68,68,0.4)" },
                                }}
                            >
                                Delete
                            </Button>
                        </Stack>
                    </Box>

                    {/* Quick stats strip */}
                    <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.12)", display: "flex", flexWrap: "wrap" }}>
                        {[
                            { label: "Subscriptions", value: user.subscriptions.length },
                            { label: "Active Sessions", value: sessionCount },
                            { label: "District", value: profile.district || "—" },
                            { label: "Mobile", value: profile.mobile || "—" },
                        ].map((stat, i) => (
                            <Box key={i} sx={{
                                flex: "1 1 120px", py: 1.5, px: 2.5,
                                borderRight: i < 3 ? "1px solid rgba(255,255,255,0.12)" : "none",
                                textAlign: "center",
                            }}>
                                <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1 }}>{stat.value}</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7, fontSize: "0.7rem" }}>{stat.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Paper>

                {/* ── Tabs content card ── */}
                <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden", animation: `${fadeUp} 0.4s ease-out 80ms both` }}>
                    <Tabs
                        value={tabIndex}
                        onChange={(_, v) => setTabIndex(v)}
                        sx={{
                            px: 2, borderBottom: "1px solid", borderColor: "divider",
                            "& .MuiTab-root": { fontWeight: 600, fontSize: "0.82rem", textTransform: "none", minHeight: 48, "&.Mui-selected": { color: PRIMARY, fontWeight: 800 } },
                            "& .MuiTabs-indicator": { background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`, height: 3, borderRadius: 2 },
                        }}
                    >
                        <Tab icon={<PersonIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Profile" />
                        <Tab icon={<StarIcon sx={{ fontSize: 16 }} />} iconPosition="start"
                            label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    Subscriptions
                                    <Chip label={user.subscriptions.length} size="small"
                                        sx={{ height: 18, fontSize: "0.65rem", fontWeight: 800, background: tabIndex === 1 ? `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` : "rgba(0,0,0,0.08)", color: tabIndex === 1 ? "#fff" : "text.secondary", "& .MuiChip-label": { px: 0.75 } }} />
                                </Box>
                            }
                        />
                        <Tab icon={<DevicesIcon sx={{ fontSize: 16 }} />} iconPosition="start"
                            label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    Sessions
                                    <Chip label={sessionCount} size="small"
                                        sx={{ height: 18, fontSize: "0.65rem", fontWeight: 800, background: tabIndex === 2 ? `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` : "rgba(0,0,0,0.08)", color: tabIndex === 2 ? "#fff" : "text.secondary", "& .MuiChip-label": { px: 0.75 } }} />
                                </Box>
                            }
                        />
                    </Tabs>

                    <Box sx={{ p: 3 }}>
                        {/* ── TAB 0: PROFILE ── */}
                        {tabIndex === 0 && (
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" fontWeight={800} color={PRIMARY} sx={{ mb: 1, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.72rem" }}>
                                        Contact Information
                                    </Typography>
                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                                        <InfoRow icon={<PhoneIcon fontSize="small" />} label="Mobile" value={profile.mobile} />
                                        <Divider />
                                        <InfoRow icon={<EmailIcon fontSize="small" />} label="Email" value={profile.email} />
                                        <Divider />
                                        <InfoRow icon={<HomeIcon fontSize="small" />} label="District" value={profile.district} />
                                        <Divider />
                                        <InfoRow icon={<HomeIcon fontSize="small" />} label="Address" value={profile.address} />
                                    </Paper>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" fontWeight={800} color={PRIMARY} sx={{ mb: 1, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.72rem" }}>
                                        Academic Information
                                    </Typography>
                                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                                        <InfoRow icon={<SchoolIcon fontSize="small" />} label="School" value={profile.school} />
                                        <Divider />
                                        <InfoRow icon={<SchoolIcon fontSize="small" />} label="Class" value={profile.className} />
                                        <Divider />
                                        <InfoRow icon={<SchoolIcon fontSize="small" />} label="Medium" value={profile.medium} />
                                        <Divider />
                                        <InfoRow
                                            icon={<PersonIcon fontSize="small" />}
                                            label="Registered"
                                            value={
                                                <Chip
                                                    label={profile.registered ? "Yes" : "No"}
                                                    size="small"
                                                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, background: profile.registered ? "#dcfce7" : "#f1f5f9", color: profile.registered ? "#166534" : "#64748b", "& .MuiChip-label": { px: 0.75 } }}
                                                />
                                            }
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}

                        {/* ── TAB 1: SUBSCRIPTIONS ── */}
                        {tabIndex === 1 && (
                            user.subscriptions.length === 0 ? (
                                <Box sx={{ py: 6, textAlign: "center" }}>
                                    <StarIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
                                    <Typography variant="h6" color="text.secondary" fontWeight={700}>No Subscriptions</Typography>
                                    <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>This user has no active subject subscriptions.</Typography>
                                    <Button variant="contained" startIcon={<GrantIcon />} onClick={handleOpenGrant} sx={{ mt: 2.5, background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, borderRadius: 2 }}>
                                        Grant Access
                                    </Button>
                                </Box>
                            ) : (
                                <Stack spacing={1.5}>
                                    {user.subscriptions.map((sub: any) => {
                                        const expired = isExpired(sub.expiry);
                                        return (
                                            <Paper key={sub.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: expired ? "#fecaca" : "#bbf7d0", background: expired ? "#fff5f5" : "#f0fdf4", "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }, transition: "box-shadow 0.15s" }}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={800} color={PRIMARY}>{sub.name}</Typography>
                                                        <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary">₹{sub.yearlyPrice}/yr</Typography>
                                                            {sub.expiry && (
                                                                <Typography variant="caption" sx={{ color: expired ? "#991b1b" : "#166534", fontWeight: 600 }}>
                                                                    {expired ? "Expired" : "Expires"} {formatDateOnly(sub.expiry)}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Chip
                                                            label={expired ? "Expired" : "Active"}
                                                            size="small"
                                                            sx={{ fontWeight: 700, fontSize: "0.7rem", background: expired ? "#fee2e2" : "#dcfce7", color: expired ? "#991b1b" : "#166534", "& .MuiChip-label": { px: 1 } }}
                                                        />
                                                        {!expired && (
                                                            <Tooltip title="Revoke this subject's access">
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="error"
                                                                    startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                                                                    onClick={() => setRevokeTarget({ subjectId: sub.id, subjectName: sub.name })}
                                                                    sx={{ fontWeight: 700, fontSize: "0.7rem", borderRadius: 1.5, py: 0.25 }}
                                                                >
                                                                    Revoke
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </Box>
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            )
                        )}

                        {/* ── TAB 2: SESSIONS ── */}
                        {tabIndex === 2 && (
                            (!profile.sessions || profile.sessions.length === 0) ? (
                                <Box sx={{ py: 6, textAlign: "center" }}>
                                    <DevicesIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
                                    <Typography variant="h6" color="text.secondary" fontWeight={700}>No Active Sessions</Typography>
                                    <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>User has not logged in from any device.</Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.5}>
                                    {profile.sessions.map((s, i) => (
                                        <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2, "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }, transition: "box-shadow 0.15s" }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                                                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                                                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                        <DevicesIcon sx={{ fontSize: 18, color: "#fff" }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={700} color={PRIMARY}>{s.deviceName || "Unknown Device"}</Typography>
                                                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace" }}>{s.deviceId}</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ textAlign: "right" }}>
                                                    <Chip
                                                        icon={s.active
                                                            ? <ActiveIcon sx={{ fontSize: "13px !important", color: "#166534 !important" }} />
                                                            : <InactiveIcon sx={{ fontSize: "13px !important", color: "#64748b !important" }} />}
                                                        label={s.active ? "Active" : "Inactive"}
                                                        size="small"
                                                        sx={{ fontWeight: 700, fontSize: "0.7rem", mb: 0.5, background: s.active ? "#dcfce7" : "#f1f5f9", color: s.active ? "#166534" : "#64748b" }}
                                                    />
                                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                                        <ScheduleIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                                                        <Typography variant="caption" color="text.disabled">Last: {formatDate(s.lastUsed)}</Typography>
                                                    </Stack>
                                                    {s.firstLogin && (
                                                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                                            <CalIcon sx={{ fontSize: 11, color: "text.disabled" }} />
                                                            <Typography variant="caption" color="text.disabled">First: {formatDate(s.firstLogin)}</Typography>
                                                        </Stack>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Stack>
                            )
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* ══ Grant Access Dialog ══ */}
            <Dialog open={grantOpen} onClose={() => setGrantOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, px: 3, py: 2.5, color: "#fff" }}>
                    <Typography variant="h6" fontWeight={800}>Grant Subject Access</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        Granting access to {profile.displayName} • Class: {profile.className || "—"}
                    </Typography>
                </Box>

                <DialogContent dividers sx={{ pt: 2.5 }}>
                    {grantLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress sx={{ color: PRIMARY }} />
                        </Box>
                    ) : availableSubjects.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <StarIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                            <Typography color="text.secondary">No new subjects available to grant.</Typography>
                            <Typography variant="caption" color="text.disabled">All subjects may already be subscribed.</Typography>
                        </Box>
                    ) : (
                        <Stack spacing={2.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Select Subjects</InputLabel>
                                <Select
                                    multiple
                                    value={grantForm.subjectIds}
                                    onChange={e => setGrantForm({ ...grantForm, subjectIds: e.target.value as number[] })}
                                    input={<OutlinedInput label="Select Subjects" />}
                                    renderValue={selected => (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {(selected as number[]).map(val => {
                                                const s = availableSubjects.find(sub => sub.id === val);
                                                return <Chip key={val} label={s?.name || val} size="small"
                                                    sx={{ background: "rgba(23,65,67,0.1)", color: PRIMARY, fontWeight: 600 }} />;
                                            })}
                                        </Box>
                                    )}
                                >
                                    {availableSubjects.map(s => (
                                        <MenuItem key={s.id} value={s.id}>
                                            <Checkbox checked={grantForm.subjectIds.includes(s.id)} size="small" sx={{ color: PRIMARY, "&.Mui-checked": { color: ACCENT } }} />
                                            <ListItemText primary={s.name} secondary={s.medium?.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                type="date" label="Access Expiry Date *" fullWidth size="small"
                                InputLabelProps={{ shrink: true }}
                                value={grantForm.expiryDate}
                                onChange={e => setGrantForm({ ...grantForm, expiryDate: e.target.value })}
                                inputProps={{ min: new Date().toISOString().split("T")[0] }}
                                helperText="The date until which the student can access the selected subjects"
                            />
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setGrantOpen(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSubmitGrant} disabled={grantLoading || availableSubjects.length === 0}
                        sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, px: 3, borderRadius: 2 }}>
                        {grantLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Grant Access"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ══ Revoke Access Confirmation Dialog ══ */}
            <Dialog open={!!revokeTarget} onClose={() => !revokeLoading && setRevokeTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)', px: 3, py: 2.5, color: '#fff' }}>
                    <Typography variant="h6" fontWeight={800}>Revoke Subject Access</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>The student will lose access immediately</Typography>
                </Box>
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Revoke <strong>{user?.profile?.displayName}</strong>'s access to <strong>{revokeTarget?.subjectName}</strong>?
                        The subscription will be marked cancelled. You can re-grant access later from the same page.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setRevokeTarget(null)} color="inherit" disabled={revokeLoading}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleRevokeSubject}
                        disabled={revokeLoading}
                        sx={{ background: '#dc2626', fontWeight: 700, px: 3, borderRadius: 2, '&:hover': { background: '#b91c1c' } }}
                    >
                        {revokeLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Revoke Access'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ══ Delete Confirmation Dialog ══ */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)', px: 3, py: 2.5, color: '#fff' }}>
                    <Typography variant="h6" fontWeight={800}>Delete User</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>This action cannot be undone</Typography>
                </Box>
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to permanently delete <strong>{user?.profile?.displayName}</strong>?
                        All their subscriptions, sessions, and data will be removed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDeleteOpen(false)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            setDeleteLoading(true);
                            try {
                                await deleteUser(Number(id));
                                setSnackbar({ type: "success", msg: "User deleted successfully" });
                                setTimeout(() => navigate("/users"), 600);
                            } catch (err: any) {
                                setSnackbar({ type: "error", msg: err?.message || "Failed to delete user" });
                            } finally {
                                setDeleteLoading(false);
                                setDeleteOpen(false);
                            }
                        }}
                        disabled={deleteLoading}
                        sx={{ background: '#dc2626', fontWeight: 700, px: 3, borderRadius: 2, '&:hover': { background: '#b91c1c' } }}
                    >
                        {deleteLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete Permanently'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar(null)} severity={snackbar?.type ?? "info"} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar?.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}